import { api } from './client';
import type { Page } from './catalogs';
import type { CancellableState } from './cancellation';

export interface DebtAdjustmentItem {
  id?: string;
  line_number: number;

  debtor_contractor_id: string;
  debtor_contract_id: string;
  debtor_account_code: string;
  debtor_deal_document_type?: string | null;
  debtor_deal_document_id?: string | null;

  creditor_contractor_id: string;
  creditor_contract_id: string;
  creditor_account_code: string;
  creditor_deal_document_type?: string | null;
  creditor_deal_document_id?: string | null;

  amount: string;
  currency_amount?: string | null;
  fx_diff_amount?: string | null;
  content?: string | null;
}

export interface DebtAdjustmentListItem {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  adjustment_type: string;
  total_amount: string;
  posted: boolean;
  deletion_mark: boolean;
  external_source?: string | null;
  // Block 5.3f:
  state?: CancellableState;
  // 2026-08-22: «Контрагент» списку — дебітор першого рядка (пусто → кредитор).
  contractor_name?: string | null;
}

export interface DebtAdjustment extends DebtAdjustmentListItem {
  use_reserve: boolean;
  record_off_balance: boolean;
  operation_content: string | null;
  posted_at: string | null;
  comment: string | null;
  items: DebtAdjustmentItem[];
  // Block 5.3f:
  cancelled_at?: string | null;
  cancellation_reason?: string | null;
}

export interface DebtAdjustmentCreate {
  organization_id: string;
  document_number: string;
  document_date: string;
  adjustment_type?: string;
  use_reserve?: boolean;
  record_off_balance?: boolean;
  operation_content?: string | null;
  comment?: string | null;
  items: DebtAdjustmentItem[];
}

export type DebtAdjustmentUpdate = Partial<DebtAdjustmentCreate>;

export const debtAdjustments = {
  list: (params?: {
    organization_id?: string;
    posted?: boolean;
    contractor_id?: string;
    adjustment_type?: string;
    date_from?: string;
    date_to?: string;
    sort_by?: string;
    sort_dir?: 'asc' | 'desc';
    limit?: number;
    offset?: number;
  }) =>
    api
      .get<Page<DebtAdjustmentListItem>>('/debt-adjustments', { params })
      .then((r) => r.data),
  get: (id: string) =>
    api.get<DebtAdjustment>(`/debt-adjustments/${id}`).then((r) => r.data),
  create: (payload: DebtAdjustmentCreate) =>
    api.post<DebtAdjustment>('/debt-adjustments', payload).then((r) => r.data),
  update: (id: string, payload: DebtAdjustmentUpdate) =>
    api
      .patch<DebtAdjustment>(`/debt-adjustments/${id}`, payload)
      .then((r) => r.data),
  remove: (id: string) =>
    api.delete(`/debt-adjustments/${id}`).then(() => undefined),
  post: (id: string) =>
    api
      .post<DebtAdjustment>(`/debt-adjustments/${id}/post`)
      .then((r) => r.data),
  unpost: (id: string) =>
    api
      .post<DebtAdjustment>(`/debt-adjustments/${id}/unpost`)
      .then((r) => r.data),
  cancel: (id: string, reason: string) =>
    api.post(`/debt-adjustments/${id}/cancel`, { cancellation_reason: reason }).then((r) => r.data),
};
